/**
 * Converts a single time string (HH:MM or HH:MM-HH:MM) to 12-hour am/pm format.
 * E.g. "08:30" -> "8:30 AM", "17:00" -> "5:00 PM", "08:30 - 17:00" -> "8:30 AM - 5:00 PM"
 */
export const convertTimeTo12Hour = (timeStr: string): string => {
  const formatPart = (part: string): string => {
    const trimmedPart = part.trim()
    const match = trimmedPart.match(/^(\d{1,2}):(\d{2})$/)
    if (!match) return trimmedPart

    const hour = parseInt(match[1], 10)
    const minute = match[2]
    const period = hour >= 12 ? 'PM' : 'AM'
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour

    return `${hour12}:${minute} ${period}`
  }

  const trimmed = timeStr.trim()

  if (/\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}/.test(trimmed)) {
    const parts = trimmed.split(/\s*-\s*/)

    return parts.map((part) => formatPart(part)).join(' - ')
  }

  return formatPart(trimmed)
}

export const formatLocationHours = (hours?: Record<string, string>): string => {
  if (!hours) return 'Hours not available'

  const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

  const formatted = daysOrder
    .filter((day) => hours[day])
    .map((day) => {
      const dayName = day.charAt(0).toUpperCase() + day.slice(1, 3)

      return `${dayName}: ${convertTimeTo12Hour(hours[day])}`
    })
    .join(', ')

  return formatted || 'Closed'
}

export const formatDistance = (distance?: number): string => {
  if (!distance) return ''

  return `${distance.toFixed(1)} mi`
}

export const getTodayHours = (hours?: Record<string, string>): string => {
  if (!hours) return 'Hours not available'

  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const today = days[new Date().getDay()]

  return hours[today] || 'Closed'
}

const DAYS_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

/** Formats hours record with 12-hour am/pm for display in expanded section */
export const formatHoursForDisplay = (hours?: Record<string, string>): [string, string][] => {
  if (!hours) return []

  return DAYS_ORDER.filter((day) => hours[day]).map((day) => {
    const dayLabel = day.charAt(0).toUpperCase() + day.slice(1)
    const timeStr = convertTimeTo12Hour(hours[day])

    return [dayLabel, timeStr] as [string, string]
  })
}
